import type { UserStatus } from "../components/brand/UserStatusIcon";
import type { StatusKind } from "../components/StatusBadge/StatusBadge";

/** The account fields the status derivation reads. */
export type AccountStatusUser = {
  emailVerified?: boolean | null;
  invitationOtpValidUntil?: unknown;
  impersonateOnly?: boolean | null;
};

/**
 * One account status for the whole console (person header, member lists):
 *  - noAccount:       the profile has no User node.
 *  - impersonateOnly: no own login — used only via the linked parent account.
 *  - invited:         a user exists, the invitation is still open.
 *  - unverified:      e-mail never confirmed and no open invite → stuck.
 *  - active:          confirmed e-mail → login works.
 * There is no blocked/disabled flag in the schema, so nothing is "Blockiert".
 */
export type AccountStatus = "active" | "invited" | "unverified" | "impersonateOnly" | "noAccount";

export function accountStatus(user: AccountStatusUser | null | undefined): AccountStatus {
  if (!user) {
    return "noAccount";
  }
  if (user.impersonateOnly) {
    return "impersonateOnly";
  }
  if (user.invitationOtpValidUntil) {
    return "invited";
  }
  if (!user.emailVerified) {
    return "unverified";
  }
  return "active";
}

export const ACCOUNT_STATUS_BADGE: Record<AccountStatus, { status: StatusKind; label: string }> = {
  active: { status: "ok", label: "Aktiv" },
  invited: { status: "invite", label: "Einladung offen" },
  unverified: { status: "warn", label: "E-Mail unbestätigt" },
  impersonateOnly: { status: "info", label: "Nur über Elternaccount" },
  noAccount: { status: "neutral", label: "Kein Konto" },
};

/** Glyph + tint of the user-status icon, shared by the person header and member lists. */
export const ACCOUNT_STATUS_ICON: Record<AccountStatus, { icon: UserStatus; tint: string }> = {
  active: { icon: "full", tint: "var(--color-success-lightest)" },
  invited: { icon: "invited", tint: "var(--color-warning-lightest)" },
  unverified: { icon: "full", tint: "var(--color-warning-lightest)" },
  impersonateOnly: { icon: "impersonateOnly", tint: "var(--color-grey-lightest)" },
  noAccount: { icon: "none", tint: "var(--color-grey-lightest)" },
};
